import { useState, useRef, useEffect } from "react";

/**
 * Fetches a short-lived signed GCS URL for a ready job's audio output and
 * automatically refreshes it 5 minutes before it expires.  Falls back to the
 * proxied `/download` route for legacy disk-based jobs (no expiry).
 *
 * When a refresh occurs the hook captures the current playback position and
 * play state from `audioRef` into `pendingRestoreRef` so the caller can
 * restore them once the new `<audio>` source has loaded.
 */
export function useAudioUrlRefresh({
  jobId,
  jobStatus,
  jobOutputUrl,
  getToken,
  audioRef,
  basePath,
}: {
  jobId: number;
  jobStatus: string | undefined;
  jobOutputUrl: string | null | undefined;
  getToken: () => Promise<string | null>;
  audioRef?: React.RefObject<HTMLAudioElement | null>;
  basePath?: string;
}) {
  const resolvedBase =
    basePath ??
    (typeof import.meta !== "undefined" && import.meta.env?.BASE_URL
      ? import.meta.env.BASE_URL.replace(/\/$/, "")
      : "");

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioExpired, setAudioExpired] = useState(false);
  const pendingRestoreRef = useRef<{ time: number; wasPlaying: boolean } | null>(null);
  // Diagnostics: when the signed URL was last (re)fetched, and when it expires.
  // Used by the playback-error telemetry path to distinguish "URL expired
  // mid-stream" from "fresh URL but GCS rejected the Range request".
  const lastRefreshAtRef = useRef<number | null>(null);
  const expiresAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (jobStatus !== "ready" || !jobOutputUrl) return;

    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let isLegacyFallback = false;
    let isFetching = false;
    let currentExpiresAt: number | null = null;

    const fetchUrl = async (isRefresh: boolean) => {
      if (cancelled || isFetching) return;
      isFetching = true;
      try {
        const token = await getToken();
        if (cancelled) return;
        const headers: Record<string, string> = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        const res = await fetch(
          `${resolvedBase}/api/jobs/${jobId}/audio-url`,
          { headers },
        );
        if (cancelled) return;
        if (res.ok) {
          const { url, expiresAt } = (await res.json()) as {
            url: string;
            expiresAt: string;
          };
          if (cancelled) return;

          currentExpiresAt = new Date(expiresAt).getTime();
          expiresAtRef.current = currentExpiresAt;
          lastRefreshAtRef.current = Date.now();

          if (isRefresh && audioRef?.current) {
            const audio = audioRef.current;
            pendingRestoreRef.current = {
              time: audio.currentTime,
              wasPlaying: !audio.paused && !audio.ended,
            };
          }

          setAudioUrl(url);

          if (refreshTimer) clearTimeout(refreshTimer);
          const expiresIn = currentExpiresAt - Date.now();
          const refreshIn = Math.max(expiresIn - 5 * 60 * 1000, 60 * 1000);
          refreshTimer = setTimeout(() => {
            if (!cancelled && !isLegacyFallback) fetchUrl(true);
          }, refreshIn);
        } else if (res.status === 410) {
          if (!cancelled) setAudioExpired(true);
        } else if (res.status === 404) {
          isLegacyFallback = true;
          if (!cancelled)
            setAudioUrl(`${resolvedBase}/api/jobs/${jobId}/download`);
        }
      } finally {
        isFetching = false;
      }
    };

    fetchUrl(false);

    const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        !cancelled &&
        !isLegacyFallback &&
        currentExpiresAt !== null &&
        currentExpiresAt - Date.now() < REFRESH_THRESHOLD_MS
      ) {
        fetchUrl(true);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [jobStatus, jobOutputUrl, jobId, getToken, resolvedBase]);

  return {
    audioUrl,
    setAudioUrl,
    audioExpired,
    setAudioExpired,
    pendingRestoreRef,
    lastRefreshAtRef,
    expiresAtRef,
  };
}
